import { useEffect, useRef, useState } from 'react';

export type SaveStatus = 'idle' | 'pending' | 'saving' | 'saved' | 'error';

/** Design 2026-10-31: save 1.5 s after the last input. */
export const AUTOSAVE_DELAY_MS = 1500;

/**
 * Debounced, strictly sequential saving of one draft at a time. Framework
 * free so it can be tested with fake timers; `useAutosave` wraps it.
 *
 * - `schedule` replaces the waiting draft and restarts the delay.
 * - `flush` saves a waiting draft now (chapter switch) and tells whether it
 *   went through.
 * - A failed draft stays queued unless something newer arrived, so the next
 *   input or flush retries it; `cancel` drops it (after a conflict the
 *   user's version lives on in "Ihre Fassung").
 */
export class AutosaveQueue<T> {
  private pending: { draft: T } | undefined;
  private timer: ReturnType<typeof setTimeout> | undefined;
  private running: Promise<boolean> | undefined;

  constructor(
    private readonly options: {
      delayMs: number;
      save: (draft: T) => Promise<void>;
      onStatus: (status: SaveStatus) => void;
      onError: (error: unknown, draft: T) => void;
    },
  ) {}

  schedule(draft: T): void {
    this.pending = { draft };
    this.options.onStatus('pending');
    this.clearTimer();
    this.timer = setTimeout(() => {
      this.timer = undefined;
      void this.run();
    }, this.options.delayMs);
  }

  async flush(): Promise<boolean> {
    this.clearTimer();
    // A save that was already running and failed counts as a failed flush,
    // even when its error handler emptied the queue (conflict).
    if (this.running && !(await this.running)) return false;
    return this.pending ? this.run() : true;
  }

  cancel(): void {
    this.clearTimer();
    this.pending = undefined;
    this.options.onStatus('idle');
  }

  dispose(): void {
    this.clearTimer();
  }

  private clearTimer(): void {
    if (this.timer !== undefined) clearTimeout(this.timer);
    this.timer = undefined;
  }

  private async run(): Promise<boolean> {
    while (this.running) await this.running;
    const next = this.pending;
    if (!next) return true;
    this.pending = undefined;
    this.options.onStatus('saving');
    const attempt = this.options.save(next.draft).then(
      () => {
        this.options.onStatus(this.pending ? 'pending' : 'saved');
        return true;
      },
      (error: unknown) => {
        if (!this.pending) this.pending = next;
        this.options.onStatus('error');
        this.options.onError(error, next.draft);
        return false;
      },
    );
    this.running = attempt;
    try {
      return await attempt;
    } finally {
      if (this.running === attempt) this.running = undefined;
    }
  }
}

export function useAutosave<T>(
  delayMs: number,
  save: (draft: T) => Promise<void>,
  onError: (error: unknown, draft: T) => void,
): { status: SaveStatus; queue: AutosaveQueue<T> } {
  const [status, setStatus] = useState<SaveStatus>('idle');
  const saveRef = useRef(save);
  const errorRef = useRef(onError);
  useEffect(() => {
    saveRef.current = save;
    errorRef.current = onError;
  });
  const [queue] = useState(
    () =>
      new AutosaveQueue<T>({
        delayMs,
        save: (draft) => saveRef.current(draft),
        onError: (error, draft) => errorRef.current(error, draft),
        onStatus: setStatus,
      }),
  );
  // Leaving the page (e.g. to the preview) must not drop a waiting draft:
  // save it right away instead of letting the timer die with the component.
  useEffect(
    () => () => {
      void queue.flush();
      queue.dispose();
    },
    [queue],
  );
  return { status, queue };
}
